import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import EnhancedSearch from './EnhancedSearch';
import NotificationSystem from './NotificationSystem';
import RecruiterTermsModal from './Company/RecruiterTermsModal';
import PostJobChoiceModal from './Company/PostJobChoiceModal';
import chatService from '../services/chatService';
import './Header.css';

const Header = ({ onLoginClick, onSignupClick, isLoggedIn, user, onBackClick, onHomeClick, onJobsClick, onCategoriesClick, onHowItWorksClick, onProfileClick, onAccountSettingsClick, onForumClick, currentPage, isHomePage = false }) => {
  const { logout, user: authUser, switchMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPostJobChoiceModal, setShowPostJobChoiceModal] = useState(false);
  const [pendingSwitchToCompany, setPendingSwitchToCompany] = useState(false);
  const [pendingCompanyUserId, setPendingCompanyUserId] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const currentMode = authUser?.active_mode || authUser?.default_mode || 'personal';
  const isCompanyMode = currentMode === 'company';
  const isRecruiterMode = currentMode === 'recruiter';
  const isRecruiterOrCompanyMode = isCompanyMode || isRecruiterMode;
  const termsAccepted = authUser?.job_posting_terms_accepted || false;
  // When in company mode, show company name as the logged-in identity (company user has no password; parent is creator)
  const displayName = (isCompanyMode && (authUser?.company_profile?.name || authUser?.active_company?.name))
    ? (authUser.company_profile?.name || authUser.active_company?.name)
    : (user?.name || 'Profile');
  
  // Extract first name for "Switch to [Firstname]" text. In company mode show parent's first name so user knows where they switch back to.
  const getUserFirstName = () => {
    if (isCompanyMode && authUser?.parent_user_id && authUser?.parent_name) {
      const nameParts = String(authUser.parent_name).trim().split(/\s+/);
      return nameParts[0] || 'Personal';
    }
    const name = authUser?.name || user?.name || '';
    const nameParts = name.trim().split(/\s+/);
    return nameParts[0] || 'Personal';
  };

  const handlePostJobsClick = () => {
    // In recruiter or company mode: switch to personal mode
    if (isRecruiterOrCompanyMode) {
      handleModeSwitch();
      return;
    }
    // In personal mode: show choice modal (create company vs post directly)
    setShowProfileDropdown(false);
    setShowPostJobChoiceModal(true);
  };

  const handleModeSwitch = async () => {
    if (isSwitchingMode) return;
    await switchModeDirectly('personal', false, true);
  };

  const handlePostJobDirect = async () => {
    setShowPostJobChoiceModal(false);
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }
    await switchModeDirectly('recruiter', false, true);
  };

  const switchModeDirectly = async (mode, acceptTerms = false, setAsDefault = false, companyUserId = null) => {
    setIsSwitchingMode(true);
    try {
      const result = await switchMode(mode, acceptTerms, setAsDefault, companyUserId);
      if (!result.success) {
        alert(result.message || 'Failed to switch mode');
      } else {
        setShowProfileDropdown(false);
        setShowPostJobChoiceModal(false);
        if (location.pathname.startsWith('/profile')) {
          window.location.reload();
        } else {
          navigate('/profile');
        }
      }
    } catch (error) {
      alert(error.message || 'Failed to switch mode');
    } finally {
      setIsSwitchingMode(false);
    }
  };

  const handleTermsAccept = async () => {
    setShowTermsModal(false);
    const switchToCompany = pendingSwitchToCompany;
    const companyId = pendingCompanyUserId;
    setPendingSwitchToCompany(false);
    setPendingCompanyUserId(null);
    await switchModeDirectly(switchToCompany ? 'company' : 'recruiter', true, true, switchToCompany ? companyId : null);
  };

  // When returning from company setup, open Post Job modal so new company appears in list
  useEffect(() => {
    if (location.state?.openPostJobChoice) {
      setShowPostJobChoiceModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openPostJobChoice, location.pathname, navigate]);

  // Update search input value from URL params when on talent search page
  useEffect(() => {
    if (location.pathname === '/talent-search') {
      const searchParam = searchParams.get('search') || '';
      setSearchInputValue(searchParam);
    } else {
      setSearchInputValue('');
    }
  }, [location.pathname, searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Helper function to handle navigation with Ctrl/Cmd+Click support
  const handleNavigationWithModifier = (path, handler, e) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    
    if (isModifierPressed) {
      // Open in new tab
      e?.preventDefault();
      const url = path.startsWith('http') ? path : `${window.location.origin}${path}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Normal navigation - use provided handler or navigate
      if (handler) {
        handler(e);
      } else {
        navigate(path);
      }
    }
  };

  // Get suspension notice from user data
  const suspensionNotice = authUser?.suspension_notice;
  
  // Debug: Log suspension notice to help troubleshoot
  useEffect(() => {
    if (authUser) {
      console.log('Header - Auth User:', authUser);
      console.log('Header - Suspension Notice:', suspensionNotice);
      console.log('Header - Suspension Notice Type:', suspensionNotice?.type);
      console.log('Header - Should Show Banner:', suspensionNotice && suspensionNotice.type === 'temporary');
    }
  }, [authUser, suspensionNotice]);

  // Fetch unread message count only on login
  useEffect(() => {
    if (!isLoggedIn || !authUser) {
      setUnreadMessageCount(0);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        const response = await chatService.getUnreadCounts();
        if (response.success && response.data) {
          // Sum all unread counts from all rooms
          const totalUnread = Object.values(response.data).reduce((sum, count) => sum + (count || 0), 0);
          setUnreadMessageCount(totalUnread);
        }
      } catch (error) {
        console.error('Error fetching unread message count:', error);
      }
    };

    // Fetch only once when user logs in
    fetchUnreadCount();

    // Listen for unread count updates via Pusher
    const handleUnreadCountUpdate = (event) => {
      const data = event.detail;
      if (data) {
        if (data.total_unread !== undefined) {
          setUnreadMessageCount(data.total_unread);
        } else if (data.unread_counts) {
          // Calculate total from unread_counts object
          const totalUnread = Object.values(data.unread_counts).reduce((sum, count) => sum + (count || 0), 0);
          setUnreadMessageCount(totalUnread);
        }
      }
    };

    window.addEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);

    return () => {
      window.removeEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
    };
  }, [isLoggedIn, authUser?.id]); // Only re-run when login status or user ID changes

  return (
    <>
      {/* Suspension Notice Banner */}
      {suspensionNotice && suspensionNotice.type === 'temporary' && (
        <div className="suspension-notice-banner">
          <div className="notice-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="notice-icon">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <div className="notice-text">
              <span className="notice-message">
                Account Temporarily Suspended
                {suspensionNotice.duration_text && (
                  <span className="notice-duration">
                    {' '}• {suspensionNotice.duration_text}
                  </span>
                )}
                {!suspensionNotice.duration_text && suspensionNotice.suspended_until_formatted && (
                  <span className="notice-duration">
                    {' '}• until {suspensionNotice.suspended_until_formatted}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      )}
      <header className={`header ${suspensionNotice && suspensionNotice.type === 'temporary' ? 'header-with-notice' : ''} ${isHomePage ? 'header-home-page' : ''}`} style={suspensionNotice && suspensionNotice.type === 'temporary' ? { top: '48px' } : {}}>
        <div className="container">
        <div className="nav-brand">
          {onBackClick ? (
            <button type="button" onClick={onBackClick} className="back-button">
              ← Back to Home
            </button>
          ) : (
            <div className="brand-logo" onClick={(e) => {
              const isModifierPressed = e?.ctrlKey || e?.metaKey;
              if (isModifierPressed) {
                e?.preventDefault();
                window.open(`${window.location.origin}/`, '_blank', 'noopener,noreferrer');
              } else {
                if (onHomeClick) {
                  onHomeClick(e);
                } else {
                  window.location.href = '/';
                }
              }
            }}>
              <div className="logo-icon">
                <img src="/main-logo-svg.svg" alt="Remote Birdy Logo" />
              </div>
              {(!isLoggedIn || isHomePage) && (
                <h1 className="brand-name">
                  <span className="brand-remote">Remote</span> <span className="brand-birdy">Birdy</span>
                </h1>
              )}
              
              {isLoggedIn && !isHomePage && (
                <div className="header-search" onClick={(e) => e.stopPropagation()}>
                  <form 
                    className="search-input-group"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const query = searchInputValue?.trim() || '';
                      if (query) {
                        navigate(`/talent-search?search=${encodeURIComponent(query)}`);
                      } else {
                        // If empty, navigate without search param
                        navigate('/talent-search');
                      }
                    }}
                  >
                    <div className="search-field">
                      <input
                        type="text"
                        name="search"
                        placeholder="Search for talent, skills, or categories..."
                        className="search-input"
                        value={searchInputValue}
                        onChange={(e) => setSearchInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const query = e.target.value.trim();
                            if (query) {
                              navigate(`/talent-search?search=${encodeURIComponent(query)}`);
                            } else {
                              // If empty, navigate without search param
                              navigate('/talent-search');
                            }
                          }
                        }}
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="search-btn"
                      onClick={(e) => {
                        // Ensure form submission works on button click
                        e.preventDefault();
                        const query = searchInputValue?.trim() || '';
                        if (query) {
                          navigate(`/talent-search?search=${encodeURIComponent(query)}`);
                        } else {
                          // If empty, navigate without search param
                          navigate('/talent-search');
                        }
                      }}
                    >
                      <i className="bi bi-search"></i>
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="nav-actions">
          {isLoggedIn ? (
            <>
              {/* Desktop Textual Navigation - Only visible on desktop */}
              <nav className="desktop-nav-menu">
                <button 
                  type="button"
                  className={`desktop-nav-link ${location.pathname === '/talent-search' || location.pathname === '/all-talents' ? 'active' : ''}`}
                  onClick={(e) => {
                    const isModifierPressed = e?.ctrlKey || e?.metaKey;
                    if (isModifierPressed) {
                      e?.preventDefault();
                      window.open(`${window.location.origin}/talent-search`, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate('/talent-search');
                    }
                  }}
                >
                  Talents
                </button>
                <button 
                  type="button"
                  className={`desktop-nav-link ${location.pathname === '/vacancies' || location.pathname === '/all-jobs' || location.pathname.startsWith('/vacancies/') || location.pathname.startsWith('/job/') ? 'active' : ''}`}
                  onClick={(e) => {
                    const isModifierPressed = e?.ctrlKey || e?.metaKey;
                    if (isModifierPressed) {
                      e?.preventDefault();
                      window.open(`${window.location.origin}/vacancies`, '_blank', 'noopener,noreferrer');
                    } else {
                      if (onJobsClick) {
                        onJobsClick(e);
                      } else {
                        navigate('/vacancies');
                      }
                    }
                  }}
                >
                  Jobs
                </button>
              </nav>
              
              {/* Mobile Icon Buttons - Only visible on mobile */}
              <button 
                className="home-btn mobile-only-btn"
                onClick={(e) => {
                  const isModifierPressed = e?.ctrlKey || e?.metaKey;
                  if (isModifierPressed) {
                    e?.preventDefault();
                    window.open(`${window.location.origin}/`, '_blank', 'noopener,noreferrer');
                  } else {
                    if (onHomeClick) {
                      onHomeClick(e);
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }
                }}
                title="Home"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9,22 9,12 15,12 15,22"></polyline>
                </svg>
              </button>
              
              <button 
                className="jobs-btn mobile-only-btn"
                onClick={(e) => {
                  const isModifierPressed = e?.ctrlKey || e?.metaKey;
                  if (isModifierPressed) {
                    e?.preventDefault();
                    window.open(`${window.location.origin}/vacancies`, '_blank', 'noopener,noreferrer');
                  } else {
                    if (onJobsClick) {
                      onJobsClick(e);
                    } else {
                      navigate('/vacancies');
                    }
                  }
                }}
                title="Jobs"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </button>
              
              <button 
                className="messages-btn"
                onClick={() => navigate('/chat')}
                title="Messages"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                {unreadMessageCount > 0 && location.pathname !== '/chat' && (
                  <span className="notification-badge chat-badge">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </button>
              
              <NotificationSystem />
              
              <div className="profile-dropdown">
              <button 
                className="profile-btn desktop-profile-btn"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                title="Profile"
              >
                <div className="profile-icon mobile-profile-icon">
                  {isCompanyMode && authUser?.active_company?.logo_url ? (
                    <img src={authUser.active_company.logo_url} alt={displayName} />
                  ) : user?.profile_photo_url ? (
                    <img src={user.profile_photo_url} alt={displayName} />
                  ) : (
                    <div className="default-avatar">
                      <span className="avatar-initial">
                        {displayName ? displayName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                  )}
                </div>
                <span className="desktop-profile-name">{displayName ? displayName.split(' ')[0] : 'Profile'}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </button>
              
              {showProfileDropdown && (
                <div className="profile-dropdown-menu">
                  <button className="dropdown-item" onClick={(e) => {
                    const isModifierPressed = e?.ctrlKey || e?.metaKey;
                    if (isModifierPressed) {
                      e?.preventDefault();
                      window.open(`${window.location.origin}/profile`, '_blank', 'noopener,noreferrer');
                    } else {
                      if (onProfileClick) {
                        onProfileClick(e);
                      } else {
                        navigate('/profile');
                      }
                    }
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Dashboard
                  </button>
                  <button 
                    className={`dropdown-item ${currentPage === 'my-jobs' ? 'active' : ''}`} 
                    onClick={(e) => {
                      const isModifierPressed = e?.ctrlKey || e?.metaKey;
                      if (isModifierPressed) {
                        e?.preventDefault();
                        window.open(`${window.location.origin}/my-jobs`, '_blank', 'noopener,noreferrer');
                      } else {
                        navigate('/my-jobs');
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                    My Jobs
                  </button>
                  <div className="dropdown-divider"></div>
                  <button 
                    className={`dropdown-item ${!isRecruiterOrCompanyMode ? 'post-jobs-item' : ''}`}
                    onClick={handlePostJobsClick}
                    disabled={isSwitchingMode}
                  >
                    {isSwitchingMode ? (
                      <>
                        <span className="spinner"></span>
                        Switching...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {isRecruiterOrCompanyMode ? (
                            <>
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </>
                          ) : (
                            <>
                              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
                              <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
                              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
                              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
                            </>
                          )}
                        </svg>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isRecruiterOrCompanyMode
                            ? (isRecruiterMode ? 'Find Job' : getUserFirstName())
                            : 'Post Jobs'}
                          {isRecruiterOrCompanyMode && (
                            <span className="blinking-green-dot"></span>
                          )}
                        </span>
                      </>
                    )}
                  </button>
                  {onAccountSettingsClick && (
                    <button 
                      className={`dropdown-item ${currentPage === 'settings' ? 'active' : ''}`} 
                      onClick={(e) => {
                        const isModifierPressed = e?.ctrlKey || e?.metaKey;
                        if (isModifierPressed) {
                          e?.preventDefault();
                          window.open(`${window.location.origin}/settings`, '_blank', 'noopener,noreferrer');
                        } else {
                          if (onAccountSettingsClick) {
                            onAccountSettingsClick(e);
                          } else {
                            navigate('/settings');
                          }
                        }}
                      }
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      Settings
                    </button>
                  )}
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout} disabled={isLoggingOut}>
                    {isLoggingOut ? (
                      <>
                        <span className="spinner"></span>
                        Logging Out...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16,17 21,12 16,7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                        Logout
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            </>
          ) : (
            <>
              <button 
                className={`btn ${currentPage === 'login' ? 'btn-primary' : 'btn-outline'}`} 
                onClick={(e) => {
                  const isModifierPressed = e?.ctrlKey || e?.metaKey;
                  if (isModifierPressed) {
                    e?.preventDefault();
                    window.open(`${window.location.origin}/login`, '_blank', 'noopener,noreferrer');
                  } else {
                    if (onLoginClick) {
                      onLoginClick(e);
                    } else {
                      navigate('/login');
                    }
                  }
                }}
              >
                Login
              </button>
              <button 
                className={`btn ${currentPage === 'signup' ? 'btn-primary' : 'btn-outline'}`} 
                onClick={(e) => {
                  const isModifierPressed = e?.ctrlKey || e?.metaKey;
                  if (isModifierPressed) {
                    e?.preventDefault();
                    window.open(`${window.location.origin}/signup`, '_blank', 'noopener,noreferrer');
                  } else {
                    if (onSignupClick) {
                      onSignupClick(e);
                    } else {
                      navigate('/signup');
                    }
                  }
                }}
              >
                Sign Up
              </button>
              <button 
                className="find-btn" 
                onClick={() => setShowSearch(!showSearch)}
                title="Find"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                Find
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Enhanced Search Modal */}
      <EnhancedSearch 
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
      />

      {/* Recruiter Terms Modal */}
      <RecruiterTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
      />

      {/* Post Job choice: list companies, post directly, or create company */}
      <PostJobChoiceModal
        isOpen={showPostJobChoiceModal}
        onClose={() => setShowPostJobChoiceModal(false)}
        onPostJobDirect={handlePostJobDirect}
        onCreateCompany={() => {
          setShowPostJobChoiceModal(false);
          navigate('/company/setup');
        }}
        onSelectCompany={(company) => {
          if (!termsAccepted) {
            setShowPostJobChoiceModal(false);
            setPendingCompanyUserId(company?.id ?? null);
            setPendingSwitchToCompany(true);
            setShowTermsModal(true);
            return;
          }
          switchModeDirectly('company', false, true, company?.id ?? null);
        }}
      />
      </header>
    </>
  );
};

export default Header;
